import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  ChevronDown,
  FolderPlus,
  Pencil,
  Trash2,
  Folder,
  Images,
} from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import AlbumDialog, { albumDialogRequest, type AlbumDialogTarget } from "./AlbumDialog";

export interface AlbumFolder {
  id: string;
  name: string;
  parentId: string | null;
  count: number;
}

interface TreeNode extends AlbumFolder {
  children: TreeNode[];
}

function buildTree(folders: AlbumFolder[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(folders.map((f) => [f.id, { ...f, children: [] }]));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) byId.get(node.parentId)!.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * Reusable album (media_folders) tree. Used as a filter in both MediaPage and the
 * MediaPicker library tab; pass `manage` to expose create/rename/delete controls.
 * Založení a přejmenování jdou přes dialog aplikace (`AlbumDialog`), nikdy přes nativní
 * `prompt`; `manage` proto smí jen mimo formulář (dialog je `<form>`).
 */
export default function AlbumTree({
  selectedId,
  onSelect,
  manage = false,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  manage?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["media-folders"],
    queryFn: () => api.get<ApiResponse<AlbumFolder[]>>("/api/admin/media-folders"),
  });
  const folders = useMemo(() => data?.data ?? [], [data]);
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["media-folders"] });

  const [dialog, setDialog] = useState<AlbumDialogTarget | null>(null);
  // Založení i přejmenování sdílí jednu mutaci; `reset()` při otevření i zavření dialogu,
  // jinak by chyba z dřívějšího pokusu visela v dialogu jiného alba.
  const saveMutation = useMutation({
    mutationFn: ({ target, name }: { target: AlbumDialogTarget; name: string }) => {
      const req = albumDialogRequest(target, name);
      return req.method === "post" ? api.post(req.path, req.body) : api.patch(req.path, req.body);
    },
    onSuccess: () => {
      invalidate();
      setDialog(null);
    },
  });
  const openDialog = (target: AlbumDialogTarget) => {
    saveMutation.reset();
    setDialog(target);
  };
  const closeDialog = () => {
    saveMutation.reset();
    setDialog(null);
  };
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/media-folders/${id}`),
    onSuccess: (_res, id) => {
      if (selectedId === id) onSelect(null);
      invalidate();
    },
  });

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: TreeNode, depth: number) => {
    const isOpen = expanded.has(node.id);
    const hasChildren = node.children.length > 0;
    return (
      <li key={node.id}>
        <div
          className={`group flex items-center gap-1 rounded px-1.5 py-1 text-sm ${
            selectedId === node.id
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
        >
          <button
            type="button"
            onClick={() => hasChildren && toggle(node.id)}
            className={`shrink-0 ${hasChildren ? "" : "invisible"}`}
            aria-label={isOpen ? t("media.albums.collapse") : t("media.albums.expand")}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            className="flex flex-1 items-center gap-1.5 truncate text-left"
          >
            <Folder className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{node.name}</span>
            <span className="text-xs text-gray-400">{node.count}</span>
          </button>
          {manage && (
            <span className="flex shrink-0 gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
              <button
                type="button"
                data-album-action="createChild"
                title={t("media.albums.newSubAlbum")}
                aria-label={t("media.albums.newSubAlbum")}
                onClick={() =>
                  openDialog({ mode: "create", parentId: node.id, parentName: node.name })
                }
              >
                <FolderPlus className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                data-album-action="rename"
                title={t("media.albums.rename")}
                aria-label={t("media.albums.rename")}
                onClick={() => openDialog({ mode: "rename", id: node.id, name: node.name })}
              >
                <Pencil className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                data-album-action="delete"
                title={t("media.albums.deleteAlbum")}
                aria-label={t("media.albums.deleteAlbum")}
                onClick={() => {
                  if (confirm(t("media.albums.confirmDelete", { name: node.name })))
                    deleteMutation.mutate(node.id);
                }}
              >
                <Trash2 className="text-error-400 hover:text-error-600 h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
        {isOpen && hasChildren && (
          <ul>{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`flex flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-sm ${
            selectedId === null
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
        >
          <Images className="h-3.5 w-3.5" /> {t("media.albums.allImages")}
        </button>
        {manage && (
          <button
            type="button"
            data-album-action="create"
            title={t("media.albums.newAlbum")}
            aria-label={t("media.albums.newAlbum")}
            onClick={() => openDialog({ mode: "create", parentId: null, parentName: null })}
            className="text-gray-400 hover:text-gray-700"
          >
            <FolderPlus className="h-4 w-4" />
          </button>
        )}
      </div>
      <ul>{tree.map((node) => renderNode(node, 0))}</ul>
      {manage && dialog && (
        <AlbumDialog
          // Nový cíl = nový stav pole (jméno přejmenovávaného alba, prázdné u založení).
          key={dialog.mode === "rename" ? `rename:${dialog.id}` : `create:${dialog.parentId ?? ""}`}
          target={dialog}
          pending={saveMutation.isPending}
          error={saveMutation.error}
          onSubmit={(name) => saveMutation.mutate({ target: dialog, name })}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
